import { useCallback, useMemo, useRef, useState } from 'react';
import { List, useScreenSize } from '@banhala/ui-kit';
import type { ViewToken } from 'react-native';
import type { MumulLivePost } from 'shared/src/api';
import { isNil, isNotNil } from 'shared/src/utils';
import { useIsForegroundNow } from 'shared/src/hooks';
import { useMumulLiveProfilePosts } from '../queries/useMumulLiveProfilePosts';
import { useLogMumulLiveContents } from '../hooks/useLogMumulLiveContents';
import { MumulLiveContentCard, getFirstVideo } from './MumulLiveContentCard';

const COLUMN_COUNT = 2;
const HORIZONTAL_PADDING = 16;
const GAP = 11;
const CARD_RATIO = 236 / 175;

const VIEWABILITY_CONFIG = { itemVisiblePercentThreshold: 50 };

type Props = {
  profileSno: number;
  onPressPost: (postSno: number) => void;
};

export const MumulLiveContentsGrid = ({ profileSno, onPressPost }: Props) => {
  const { width: screenWidth } = useScreenSize();
  const isForeground = useIsForegroundNow();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useMumulLiveProfilePosts(profileSno);

  const posts = useMemo(
    () => data?.pages.flatMap(page => page.post_list) ?? [],
    [data],
  );

  const cardWidth =
    (screenWidth - HORIZONTAL_PADDING * 2 - GAP * (COLUMN_COUNT - 1)) /
    COLUMN_COUNT;
  const cardHeight = cardWidth * CARD_RATIO;

  const { logDisplayMumulLivePost, logClickMumulLivePost } =
    useLogMumulLiveContents(profileSno);

  // onViewableItemsChanged 는 List 에 한 번 넘긴 identity 가 고정되므로
  // 최신 로깅 콜백은 ref 로 읽는다.
  const logDisplayPostRef = useRef(logDisplayMumulLivePost);
  logDisplayPostRef.current = logDisplayMumulLivePost;
  // 바텀시트가 닫히면 grid 가 unmount 되므로 열림 1회당 콘텐츠별 1회 발화된다.
  const loggedPostSnosRef = useRef<Set<number>>(new Set());

  // 화면에 보이는 영상 중 가장 위(= 가장 작은 index) 1개만 재생한다.
  const [playingSno, setPlayingSno] = useState<number | null>(null);
  const onViewableItemsChanged = useRef(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      const firstVideo = viewableItems.find(
        token =>
          isNotNil(token.item) &&
          isNotNil(getFirstVideo(token.item as MumulLivePost)),
      );
      setPlayingSno(
        (firstVideo?.item as MumulLivePost | undefined)?.sno ?? null,
      );

      viewableItems.forEach(({ isViewable, item }) => {
        const post = item as MumulLivePost | null;
        if (!isViewable || isNil(post)) return;
        if (loggedPostSnosRef.current.has(post.sno)) return;

        loggedPostSnosRef.current.add(post.sno);
        logDisplayPostRef.current(post);
      });
    },
  ).current;

  const handleEndReached = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const renderItem = useCallback(
    ({ item }: { item: MumulLivePost }) => (
      <MumulLiveContentCard
        post={item}
        width={cardWidth}
        height={cardHeight}
        isPlaying={playingSno === item.sno}
        isForeground={isForeground}
        onPress={() => {
          logClickMumulLivePost(item);
          onPressPost(item.sno);
        }}
      />
    ),
    [
      cardWidth,
      cardHeight,
      playingSno,
      isForeground,
      onPressPost,
      logClickMumulLivePost,
    ],
  );

  return (
    <List
      data={posts}
      keyExtractor={item => String(item.sno)}
      renderItem={renderItem}
      numColumns={COLUMN_COUNT}
      columnWrapperStyle={{ gap: GAP }}
      contentContainerStyle={{
        paddingHorizontal: HORIZONTAL_PADDING,
        paddingBottom: 40,
        gap: GAP,
      }}
      showsVerticalScrollIndicator={false}
      onEndReached={handleEndReached}
      onEndReachedThreshold={0.5}
      onViewableItemsChanged={onViewableItemsChanged}
      viewabilityConfig={VIEWABILITY_CONFIG}
    />
  );
};
